import { Fragment, useRef } from "react";
import { useTranslation } from "react-i18next";
import { cn } from "../../../lib/utils.js";
import {
  applyOwnershipOtpArrow,
  applyOwnershipOtpBackspace,
  applyOwnershipOtpInput,
  applyOwnershipOtpPaste,
  OWNERSHIP_OTP_LENGTH,
  ownershipOtpDigitsFromCode,
  serializeOwnershipOtp,
  type OwnershipOtpEdit,
} from "./ownership-otp.js";

export interface OwnershipOtpInputProps {
  value: string;
  onChange: (code: string) => void;
  labelId: string;
  disabled?: boolean;
  invalid?: boolean;
  describedBy?: string;
}

const OTP_SEPARATOR_BEFORE = OWNERSHIP_OTP_LENGTH / 2;

const slotClass =
  "h-12 w-11 rounded-md border border-line bg-surface text-center font-mono text-base tabular-nums text-ink transition-colors focus:border-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent disabled:cursor-not-allowed disabled:opacity-70 sm:h-13 sm:w-12 sm:text-lg";
const invalidSlotClass = "border-error/60 bg-error-muted/40 focus:border-error focus-visible:ring-error";

export function OwnershipOtpInput({
  value,
  onChange,
  labelId,
  disabled = false,
  invalid = false,
  describedBy,
}: OwnershipOtpInputProps): React.JSX.Element {
  const { t } = useTranslation();
  const inputsRef = useRef<Array<HTMLInputElement | null>>([]);
  const digits = ownershipOtpDigitsFromCode(value);
  const firstEmpty = digits.findIndex((digit) => digit === "");
  const autoFocusIndex = firstEmpty === -1 ? OWNERSHIP_OTP_LENGTH - 1 : firstEmpty;

  function commit(edit: OwnershipOtpEdit): void {
    onChange(serializeOwnershipOtp(edit.digits));
    if (!disabled) {
      inputsRef.current[edit.focusIndex]?.focus();
    }
  }

  return (
    <div
      role="group"
      dir="ltr"
      aria-labelledby={labelId}
      data-testid="add-phone-ownership-otp"
      className="mx-auto flex w-fit items-center gap-1 sm:gap-1.5"
    >
      {digits.map((digit, index) => (
        <Fragment key={index}>
          {index === OTP_SEPARATOR_BEFORE ? (
            <span
              aria-hidden="true"
              data-ownership-otp-separator=""
              className="w-2 select-none text-center text-base text-ink-subtle sm:w-3 sm:text-lg"
            >
              –
            </span>
          ) : null}
          <input
            ref={(node) => {
              inputsRef.current[index] = node;
            }}
            data-ownership-otp-slot={index}
            className={cn(slotClass, invalid && invalidSlotClass)}
            aria-invalid={invalid || undefined}
            aria-describedby={describedBy}
            value={digit}
            type="text"
            inputMode="numeric"
            autoComplete={index === 0 ? "one-time-code" : "off"}
            autoCapitalize="off"
            autoCorrect="off"
            spellCheck={false}
            enterKeyHint={index === OWNERSHIP_OTP_LENGTH - 1 ? "done" : "next"}
            name={index === 0 ? "one-time-code" : undefined}
            aria-label={t("whatsapp.addPhone.fields.verificationDigit", {
              position: index + 1,
              total: OWNERSHIP_OTP_LENGTH,
            })}
            disabled={disabled}
            autoFocus={index === autoFocusIndex}
            onFocus={(event) => {
              event.currentTarget.select();
            }}
            onChange={(event) => {
              commit(applyOwnershipOtpInput(digits, index, event.target.value));
            }}
            onPaste={(event) => {
              event.preventDefault();
              commit(applyOwnershipOtpPaste(digits, index, event.clipboardData.getData("text")));
            }}
            onKeyDown={(event) => {
              if (event.nativeEvent.isComposing) return;
              if (event.key === "Backspace") {
                event.preventDefault();
                commit(applyOwnershipOtpBackspace(digits, index));
                return;
              }
              if (event.key === "ArrowLeft" || event.key === "ArrowRight") {
                event.preventDefault();
                inputsRef.current[applyOwnershipOtpArrow(index, event.key)]?.focus();
              }
            }}
          />
        </Fragment>
      ))}
    </div>
  );
}
